// src/pages/CommissionsCarrierHandlePage.js
import React, { useEffect, useState } from 'react';
import {
  getCarrierCommissions,
  createCarrierCommission,
  updateCarrierCommission,
  deleteCarrierCommission
} from '../api';
import { Table, Button, Modal, Form, Spinner } from 'react-bootstrap';
import SortableHeader from '../components/ui/SortableHeader';
import { useSorting } from '../hooks/useSorting';

const CommissionsCarrierHandlePage = () => {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({
    show: false,
    isNew: false,
    saving: false,
    commission: { id: 0, code: '', name: '' }
  });
  const { orderBy, orderDesc, handleSort, getSortIcon } = useSorting();

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await getCarrierCommissions(orderBy, orderDesc);
      setCommissions(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (orderBy || orderDesc) {
      loadData();
    }
  }, [orderBy, orderDesc]);

  const openNew = () => setModal({
    show: true,
    isNew: true,
    saving: false,
    commission: { id: 0, code: '', name: '', defaultPrice: 0 }
  });

  const openEdit = (c) => setModal({
    show: true,
    isNew: false,
    saving: false,
    commission: { id: c.id, code: c.code, name: c.name, defaultPrice: c.defaultPrice }
  });

  const handleDelete = async (id) => {
    if (!window.confirm('¿Seguro que deseas borrar?')) return;
    try {
      await deleteCarrierCommission(id);
      loadData();
    } catch (err) {
      console.error(err);
      alert('No se pudo eliminar.');
    }
  };

  const handleSave = async () => {
    const { isNew, commission } = modal;
    setModal(m => ({ ...m, saving: true }));
    try {
      if (isNew) {
        await createCarrierCommission({
          code: commission.code,
          name: commission.name,
          defaultPrice: Number(commission.defaultPrice)
        });
      } else {
        await updateCarrierCommission(commission.id, {
          id: commission.id,
          code: commission.code,
          name: commission.name,
          defaultPrice: Number(commission.defaultPrice)
        });
      }
      setModal(m => ({ ...m, show: false }));
      loadData();
    } catch (err) {
      console.error(err);
      alert('Error al guardar.');
    } finally {
      setModal(m => ({ ...m, saving: false }));
    }
  };

  if (loading) {
    return (
      <div className="container my-4 text-center">
        <Spinner animation="border" /> Cargando…
      </div>
    );
  }

  return (
    <div className="container my-4">
      <h2 className="mb-4">Gestión de productos para tarifas de transportistas</h2>
      <Button variant="primary" onClick={openNew} className="mb-3">
        Añadir Producto
      </Button>

      <div className="table-responsive">
        <Table striped bordered hover>
          <thead>
            <tr>
              <SortableHeader column="id" onSort={handleSort} getSortIcon={getSortIcon}>ID</SortableHeader>
              <SortableHeader column="code" onSort={handleSort} getSortIcon={getSortIcon}>Nombre</SortableHeader>
              <SortableHeader column="name" onSort={handleSort} getSortIcon={getSortIcon}>Descripción</SortableHeader>
              <SortableHeader column="defaultPrice" onSort={handleSort} getSortIcon={getSortIcon}>Precio por defecto</SortableHeader>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {commissions.map(c => (
              <tr key={c.id}>
                <td>{c.id}</td>
                <td>{c.code}</td>
                <td>{c.name}</td>
                <td>{c.defaultPrice?.toFixed(2)}€</td>
                <td>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="me-2"
                    onClick={() => openEdit(c)}
                  >
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => handleDelete(c.id)}
                  >
                    Borrar
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <Modal show={modal.show} onHide={() => setModal(m => ({ ...m, show: false }))}>
        <Modal.Header closeButton={!modal.saving}>
          <Modal.Title>
            {modal.isNew ? 'Nuevo producto' : `Editar #${modal.commission.id}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nombre</Form.Label>
              <Form.Control
                type="text"
                value={modal.commission.code}
                onChange={e =>
                  setModal(m => ({ 
                    ...m, 
                    commission: { ...m.commission, code: e.target.value } 
                  }))
                }
                disabled={modal.saving}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                type="text"
                value={modal.commission.name}
                onChange={e =>
                  setModal(m => ({ 
                    ...m, 
                    commission: { ...m.commission, name: e.target.value } 
                  }))
                }
                disabled={modal.saving}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Precio por defecto</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={modal.commission.defaultPrice ?? ''}
                onChange={e =>
                  setModal(m => ({
                    ...m,
                    commission: { ...m.commission, defaultPrice: e.target.value }
                  }))
                }
                disabled={modal.saving}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setModal(m => ({ ...m, show: false }))}
            disabled={modal.saving}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={modal.saving}
          >
            {modal.saving
              ? <Spinner animation="border" size="sm" />
              : 'Guardar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CommissionsCarrierHandlePage;
